<template>
  <div class="p-partials-users">
    <div slot="row">
      <VMessage
        type="warning"
        :fullwidth="true"
        class="p-partials-users__message-margin"
        v-if="!permissionUsers">
        <template v-slot:icon>
          <ExclamationTriangleIcon />
        </template>
        <template v-slot:text>
          {{ $t('users.permissionWarning') }}
        </template>
      </VMessage>
      <div v-else>
        <div class="inline-flex gap-base justify-between justify-right controls-right w-full mb-base flex-wrap-reverse">
          <div class="users-search-container flex-grow">
            <VSearch
              class="flex-grow float-none"
              :placeholder="$t('users.searchPlaceholder')"
              data-test="users-search"
              @onSearch="onSearch"
              @onResetSearch="onResetSearch"
              data-ec="users"
              data-et="search_users_input" />
          </div>
          <VButton
            type="cta-1"
            class="float-none whitespace-nowrap"
            @click="openCreateModal"
            data-test="button-new-user"
            v-if="permissionUsers"
            data-ec="users"
            data-et="new_user_button">
            <PlusIcon />
            <span>{{ $t('users.newUser') }}</span>
          </VButton>
        </div>
        <div>
            <VMessage v-if="!results.length" type="info" subtype="outlined" :fullwidth="true">
              <template v-slot:icon>
                <ExclamationTriangleIcon />
              </template>
              <template v-slot:text>
                {{ $t('users.noUsers') }}
              </template>
            </VMessage>
            <TransitionGroup name="fade" class="grid w-full lg:grid-cols-cardGrid3 md:grid-cols-cardGrid2 grid-cols-cardGrid1 gap-base mb-4">
              <PersonCard
                  v-for="(user, index) in results"
                  :key="index"
                  v-cloak
                  @activated="!pageLoading && permissionUsers ? openViewModal($event, index) : null"
                  :loading="pageLoading"
                  :email="user.email"
                  :name="user.name">
                <template v-slot:additionalContent>
                  <div>
                    <b>{{ $t('users.teams') }}: </b>
                    <span>{{ formatUserGroups(user.groups) }}</span>
                  </div>
                  <div><b>{{ $t('users.role') }}:</b> {{ $t(`users.roles.${user.role}`) }}</div>
                </template>
              </PersonCard>
            </TransitionGroup>
          </div>
      </div>
    </div>
    <VPaginate
        v-if="$store.state.users.totalCount > $store.state.users.pagingLimit"
        storeAction="users/PAGINATE"
        :previous="$t('functional.previous')"
        :next="$t('functional.next')"
        :current="$store.getters['users/currentPage'] + 1"
        :last="$store.getters['users/totalPages']"
        :showUrl="false" />
    <div>
      <PageResultsCounter
        v-if="resultsMeta && totalCount && permissionUsers"
        :from="resultsMeta.from"
        :to="resultsMeta.to"
        :total="totalCount"
        :type="$t('functional.users')" />
    </div>

    <CreateUserModal
      :show="showCreateUserModal"
      @close="showCreateUserModal = false"
      @user-created="onUserCreated"
    />

    <ViewUserModal
      :selected-user="user.details"
      :show="showViewUserModal"
      @close="showViewUserModal = false"
      @delete-user="openDeleteUserModal"
      @reset-user-password="openPasswordResetModal"
      @edit-user="openEditUserModal"
    />

    <EditUserModal
      :selected-user="user.details"
      :show="showEditUserModal"
      @close="showEditUserModal = false"
      @user-updated="onUserChanged"
      @cancel="showViewUserModal = true"
    />

    <VDynamicLoader
      viewURL="modals/user/DeleteUserModal"
      @onUserDeleted="onUserDeleted"
      @cancel="showViewUserModal = true"
      :data="{
        user: user.details,
        storeAction: 'users/DELETE',
      }"
    />
    <VDynamicLoader
      viewURL="modals/user/ResetUserPasswordModal"
      @cancel="showViewUserModal = true"
      :data="{
        user: user.details
      }"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VPaginate from '@/components/deprecated/molecules/Paginate.vue';
import VSearch from '@/components/deprecated/organisms/Search.vue';
import PageResultsCounter from '@/views/components/PageResultsCounter.vue';
import { logAmplitudeEvent } from '@/utils/analytics';
import PersonCard from '@/components/tailwind/PersonCard.vue';
import CreateUserModal from '@/components/tailwind/modals/users/CreateUserModal.vue';
import EditUserModal from '@/components/tailwind/modals/users/EditUserModal.vue';
import ViewUserModal from '@/components/tailwind/modals/users/ViewUserModal.vue';
import VDynamicLoader from '@/components/DynamicLoader.vue';
import formatUserGroupsMixin from '@/mixins/formatUserGroups';
import { ExclamationTriangleIcon, PlusIcon } from '@/components/icons/fontIcons';

export default {
  name: 'UsersPartial',
  components: {
    ViewUserModal,
    EditUserModal,
    CreateUserModal,
    VButton,
    VMessage,
    PersonCard,
    VSearch,
    VPaginate,
    PageResultsCounter,
    VDynamicLoader,
    PlusIcon,
    ExclamationTriangleIcon,
  },
  mixins: [formatUserGroupsMixin],
  data() {
    return {
      showCreateUserModal: false,
      showEditUserModal: false,
      showViewUserModal: false,
      currentPage: 1,
      searchResultsCurrentQuery: null,
      storeAction: '',
      loading: true,
      user: {
        paginated: {
          data: null,
          loading: false,
        },
        details: {
          name: null,
          email: null,
          selectedTeam: null,
          groups: [],
          role: null,
        },
      },
    };
  },
  mounted() {
    this.loadData(this.currentPage);
  },
  watch: {
    currentPage(newVal) {
      this.loadData(newVal);
    },
  },
  computed: {
    ...mapGetters({
      userHasRole: 'roles/userHasRole',
      totalPages: 'users/totalPages',
      usersPage: 'users/pagedResults',
      resultsMeta: 'users/meta',
      totalCount: 'users/totalCount',
      hasLoaded: 'users/hasLoaded',
    }),
    results() {
      if (this.loading) {
        return new Array(60).fill({ name: '', email: '' });
      }
      return this.usersPage;
    },
    isMobileSize() {
      return !!(screen.width < 450); // eslint-disable-line
    },
    pageLoading() {
      if (this.loading || !this.hasLoaded) {
        return true;
      } else if (!this.results[0]) {
        return true;
      }
      return false;
    },
    permissionUsers() {
      return this.userHasRole(['super-admin', 'admin']);
    },
    responsiveNoOfPages() {
      return (screen.width > 666) ? 45 : 10; // eslint-disable-line
    },
  },
  methods: {
    loadData(page) {
      const actions = [];
      actions.push(this.getUsers(page));
      this.useLoading(actions);
    },
    onUserChanged() {
      this.showEditUserModal = false;
      logAmplitudeEvent('users-user-updated');
      this.useLoading([
        this.useDelay(1000, () => this.getUsers(this.currentPage)),
      ]);
    },
    onUserCreated() {
      this.showCreateUserModal = false;
      logAmplitudeEvent('users-user-created');
    },
    onUserDeleted() {
      logAmplitudeEvent('users-user-deleted');
      this.useLoading([
        this.useDelay(1000, () => this.getUsers(this.currentPage)),
      ]);
    },
    onSearch({ value }) {
      logAmplitudeEvent('users-search-searched');
      this.searchResultsCurrentQuery = value;
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      } else {
        this.loadData(1);
      }
    },
    onResetSearch() {
      logAmplitudeEvent('users-search-reset');
      this.$router.replace({ query: {} });
      this.searchResultsCurrentQuery = null;
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      } else {
        this.loadData(1);
      }
    },
    resetUserDetails() {
      this.user.details = {
        name: null,
        email: null,
        selectedTeam: null,
        roles: null,
        groups: [],
      };
    },
    openCreateModal() {
      logAmplitudeEvent('users-user-create-modal-opened');
      this.showCreateUserModal = true;
    },
    openViewModal($event, originalIndex) {
      logAmplitudeEvent('users-user-view-modal-opened');
      let index;
      if (originalIndex === undefined) {
        index = $event.currentTarget.getAttribute('data-id');
      } else {
        index = originalIndex;
      }
      const currentResults = this.results;
      const user = JSON.parse(JSON.stringify(currentResults[index]));
      this.$set(this.user, 'details', user);

      this.showViewUserModal = true;
    },
    openEditUserModal() {
      logAmplitudeEvent('users-user-edit-modal-opened');
      this.showEditUserModal = true;
    },
    openDeleteUserModal() {
      this.$bus.$emit('toggleModal', { name: 'user-delete-modal', active: true });
    },
    openPasswordResetModal() {
      this.$bus.$emit('toggleModal', { name: 'user-reset-password-modal', active: true });
    },
    getUsers(page) {
      return this.$store.dispatch('users/PAGINATE', {
        page,
        pageLimit: this.responsiveNoOfPages,
        searchQuery: {
          query: this.searchResultsCurrentQuery,
        },
      });
    },
    async useDelay(delay, action) {
      await (new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, delay);
      }));
      return action();
    },
    useLoading(promises) {
      this.loading = true;
      Promise.all(promises).finally(() => {
        this.loading = false;
      });
    },
  },
};
</script>

<style scoped>

.o-search {
  float:none;
}

.o-search .a-input {
  float: none !important;
}

.controls-right {
  justify-content: right;
}
</style>
